import { clamp } from 'lodash';
import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useEvent, useToggle } from 'react-use';

import { US_LETTER_SCALE_WIDTH_PX } from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';

import { DragOriginBounds } from '../SlideConfigForm.constants';

export interface UseSlideOriginDragArgs {
  /** Current X origin (slide-coordinate pixels). */
  originX: number;
  /** Current Y origin (slide-coordinate pixels). */
  originY: number;
  /**
   * Called on every pointermove during a drag with the next integer
   * X/Y origin in slide-coordinate pixels.
   */
  onChange: (nextX: number, nextY: number) => void;
  /**
   * Ref to the rendered slide element. Used to derive the active scale
   * (rendered width ÷ unscaled canvas width) so screen-pixel deltas map
   * 1:1 to the unscaled coordinate space the origin values live in.
   */
  slideRef: RefObject<HTMLElement | null>;
  /** When true, pointer events are no-ops (e.g. inheriting divider). */
  disabled?: boolean;
  /**
   * Optional limits for the origin, read once at pointerdown from the
   * rendered layout. Each drag move stops at these limits.
   */
  getBounds?: (originX: number, originY: number) => DragOriginBounds | null;
}

interface DragSession {
  startClientX: number;
  startClientY: number;
  startOriginX: number;
  startOriginY: number;
  /** Captured at pointerdown so the scale stays stable for the session. */
  scale: number;
  /** Origin limits captured at pointerdown, or null for no limits. */
  bounds: DragOriginBounds | null;
  /**
   * Pointer that owns this session. Document-level move/up listeners
   * filter by this so a 2nd touch doesn't drive an unrelated drag.
   */
  pointerId: number;
}

/**
 * Pointer-driven drag hook for slide-element origin offsets. Captures
 * a snapshot at pointerdown, drives onChange from document-level
 * pointermove, and tears down on pointerup/cancel.
 */
export function useSlideOriginDrag({
  originX,
  originY,
  onChange,
  slideRef,
  disabled = false,
  getBounds,
}: UseSlideOriginDragArgs) {
  const [isDragging, toggleDragging] = useToggle(false);
  const sessionRef = useRef<DragSession | null>(null);

  // Always read the latest origin values inside pointerdown without
  // re-binding the handler each render.
  const originXRef = useRef(originX);
  const originYRef = useRef(originY);
  originXRef.current = originX;
  originYRef.current = originY;

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (disabled) return;
      // Only react to the primary mouse button / single-touch.
      if (event.button !== 0) return;
      // Ignore additional pointers while a drag is already in flight.
      if (sessionRef.current) return;

      const slide = slideRef.current;
      if (!slide) return;

      const rect = slide.getBoundingClientRect();
      if (rect.width <= 0) return;

      event.preventDefault();
      sessionRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        startOriginX: originXRef.current,
        startOriginY: originYRef.current,
        scale: rect.width / US_LETTER_SCALE_WIDTH_PX,
        bounds: getBounds?.(originXRef.current, originYRef.current) ?? null,
        pointerId: event.pointerId,
      };
      toggleDragging(true);
    },
    [disabled, slideRef, toggleDragging, getBounds]
  );

  // Guard each document-level handler on `sessionRef.current` rather
  // than `isDragging`: the ref is set synchronously inside pointerdown,
  // while `isDragging` only flips after the next render. A click-then-
  // release within a single frame would otherwise hit a stale
  // `isDragging=false` closure, miss the pointerup, and leave the
  // session stuck.
  useEvent(
    'pointermove',
    (e: Event) => {
      const session = sessionRef.current;
      if (!session || session.scale === 0) return;

      const pointerEvent = e as PointerEvent;
      // Filter by pointerId so other touches don't drive the drag.
      if (pointerEvent.pointerId !== session.pointerId) return;

      const dxScreen = pointerEvent.clientX - session.startClientX;
      const dyScreen = pointerEvent.clientY - session.startClientY;
      const nextX = Math.round(session.startOriginX + dxScreen / session.scale);
      const nextY = Math.round(session.startOriginY + dyScreen / session.scale);
      const { bounds } = session;
      if (!bounds) {
        onChange(nextX, nextY);
        return;
      }
      onChange(
        clamp(nextX, bounds.minX, bounds.maxX),
        clamp(nextY, bounds.minY, bounds.maxY)
      );
    },
    document
  );

  useEvent(
    'pointerup',
    (e: Event) => {
      const session = sessionRef.current;
      if (!session) return;
      const pointerEvent = e as PointerEvent;
      if (pointerEvent.pointerId !== session.pointerId) return;
      sessionRef.current = null;
      toggleDragging(false);
    },
    document
  );

  useEvent(
    'pointercancel',
    (e: Event) => {
      const session = sessionRef.current;
      if (!session) return;
      const pointerEvent = e as PointerEvent;
      if (pointerEvent.pointerId !== session.pointerId) return;
      sessionRef.current = null;
      toggleDragging(false);
    },
    document
  );

  useEffect(() => {
    if (!isDragging) return;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isDragging]);

  return { onPointerDown, isDragging };
}
